import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  successAlert,
  errorAlert,
  getOrganization,
  editOrganization,
} from 'actions';
import {
  OrganizationExtraDetailsState,
  buildChangedExtraDetailsPayload,
  defaultExtraDetailsState,
  extraDetailsApiToState,
  fetchOrganizationExtraDetails,
  getExtraDetailsErrorMessage,
  saveOrganizationExtraDetails,
} from 'app/configuration/organizations/organization-extra-details';
import { OrganizationForm } from 'app/configuration/organizations/organization-form';
import { OrganizationFormState } from 'app/configuration/organizations/organization-helpers';
import { trimTrailingSlash, useTypedSelector } from 'helpers';
import { Organization } from 'types';

type ExtraStatus = 'loading' | 'error' | 'ready';

const EditOrganization: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>();
  const baseURL = trimTrailingSlash(match.url);
  const intl = useIntl();

  const organizationId = match.params.id;

  const organizationData = useTypedSelector(
    state =>
      state.organizations.collection[organizationId] as Organization | undefined
  );

  // Extra details come from the Express API (the Ruby API does not know them).
  const [extraStatus, setExtraStatus] = useState<ExtraStatus>('loading');

  const [initialExtras, setInitialExtras] =
    useState<OrganizationExtraDetailsState>(defaultExtraDetailsState);

  // Snapshot of the Ruby inputs as first loaded. Kept frozen (instead of
  // following redux) so that, if the Express save fails right after the Ruby
  // save succeeded, Formik does not reinitialise and wipe what the user typed.
  const [rubyInitial, setRubyInitial] = useState<{
    englishName: string;
    arabicName: string;
    status: boolean;
  } | null>(null);

  // Set once the Ruby edit succeeded, so a retry after an Express failure does
  // not call the Ruby API again.
  const rubySavedRef = useRef(false);

  useEffect(() => {
    if (!organizationData) {
      dispatch(getOrganization(organizationId));
    }
  }, [dispatch, organizationData, organizationId]);

  useEffect(() => {
    if (organizationData && !rubyInitial) {
      setRubyInitial({
        englishName: organizationData.en_name,
        arabicName: organizationData.ar_name,
        status: !organizationData.disabled,
      });
    }
  }, [organizationData, rubyInitial]);

  useEffect(() => {
    let cancelled = false;

    setExtraStatus('loading');

    fetchOrganizationExtraDetails(organizationId)
      .then(data => {
        if (cancelled) return;
        setInitialExtras(extraDetailsApiToState(data));
        setExtraStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setExtraStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const initialState: OrganizationFormState = useMemo(() => {
    const rubyPart = rubyInitial ?? { englishName: '', arabicName: '' };

    return extraStatus === 'ready'
      ? { ...rubyPart, ...initialExtras }
      : rubyPart;
  }, [rubyInitial, extraStatus, initialExtras]);

  const handleSubmit = async (
    values: OrganizationFormState,
    { setSubmitting }: FormikHelpers<OrganizationFormState>
  ) => {
    setSubmitting(true);

    try {
      // 1) Ruby backend: only when one of ITS inputs changed.
      const rubyChanged =
        !rubyInitial ||
        values.englishName !== rubyInitial.englishName ||
        values.arabicName !== rubyInitial.arabicName ||
        Boolean(values.status) !== rubyInitial.status;

      if (rubyChanged && !rubySavedRef.current) {
        try {
          await Promise.resolve(
            dispatch(
              editOrganization(organizationId, {
                en_name: values.englishName,
                ar_name: values.arabicName,
                disabled: !values.status,
              })
            )
          );

          rubySavedRef.current = true;
        } catch {
          // the API interceptor already raised the alert; nothing was saved
          return;
        }
      }

      // 2) Express backend: only the extra inputs the user actually changed.
      if (extraStatus === 'ready') {
        const payload = buildChangedExtraDetailsPayload(
          values as OrganizationExtraDetailsState,
          initialExtras
        );

        if (Object.keys(payload).length > 0) {
          try {
            await saveOrganizationExtraDetails(organizationId, payload);
          } catch (error) {
            dispatch(
              errorAlert(
                `${intl.formatMessage({
                  id: 'orgExtraDetailsSaveFailed',
                })}: ${getExtraDetailsErrorMessage(error)}`
              )
            );

            return;
          }
        }
      }

      history.push(`${baseURL}`.replace('edit', ''));

      dispatch(
        successAlert(intl.formatMessage({ id: 'successEditOrganization' }))
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OrganizationForm
      {...{ initialState, handleSubmit, isEditForm: true }}
      extraDetailsStatus={extraStatus}
      allowEmptyKind={initialExtras.organizationKind === ''}
    />
  );
};

export { EditOrganization };
